import React, {FC, useEffect} from 'react';
import {Button} from 'antd';
import {TextInput} from 'components/form/TextInput';
import {useTranslation} from 'react-i18next';
import {AuthFormContainer} from 'components/AuthFormContainer';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {useForm} from 'react-hook-form';
import {CollectorAdministratorCreateDto} from '../../../../../PRMGApiClient';
import {useNavigate} from 'react-router';
import {PhoneInput} from '../../components/form/PhoneInput';
import {loadExampleOrDefaults} from '../../helpers/utils';
import {registerExampleData} from '../../exampleData/registerForm';
import {toJS} from 'mobx';
import {showError} from '../../helpers/alerts';
import {AppRoutePaths} from '../../routing/Routes';

export const RegisterView: FC = observer(() => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const user = toJS(appState.user);
    if (user && user.hasCollectorAdministratorData) {
      showError(t('User already registered'));
      navigate(AppRoutePaths.dashboard.url);
    }
  }, [navigate, t]);

  const {
    register,
    handleSubmit,
    control,
    formState: {isSubmitting, errors},
  } = useForm<CollectorAdministratorCreateDto>({
    mode: 'onTouched',
    defaultValues: loadExampleOrDefaults<CollectorAdministratorCreateDto>(registerExampleData),
  });

  return (
    <AuthFormContainer contentClass="md:w-[56rem] lg:w-[56rem] overflow-scroll items-start pt-6">
      <div className="flex flex-col items-start justify-between rounded-lg">
        <h2 className="text-3xl mb-3 h-full">{t('Registration')}</h2>
        <form className="flex flex-col content-center" onSubmit={handleSubmit((payload) => appState.register(payload))}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-12 mb-12">
            <TextInput
              label={t('First name')}
              placeholder={t('Type your name')}
              {...register('firstName', {required: true})}
              error={errors.firstName}
            />
            <TextInput
              label={t('Last Name')}
              placeholder={t('Type your last name')}
              {...register('surname', {required: true})}
              error={errors.surname}
            />
            <PhoneInput
              control={control}
              register={register}
              errors={errors.phoneNumber}
              errorsPrefix={errors.phoneNumberPrefix}
              label={t('Phone number')}
            />

            <TextInput name="company" label={t('Company name')} placeholder={t('Type your company name')} />
            <TextInput
              label={t('Street name')}
              placeholder={t('Type street name')}
              {...register('address.street', {required: true})}
              error={errors.address?.street}
            />

            <div className="grid grid-cols-2 gap-x-3">
              <TextInput
                label={t('Building number')}
                placeholder={t('Type number')}
                {...register('address.buildingNumber', {required: true})}
                error={errors.address?.buildingNumber}
              />
              <TextInput
                label={t('Apartment number')}
                placeholder={t('Type number')}
                {...register('address.apartmentNumber', {required: true})}
                error={errors.address?.apartmentNumber}
              />
            </div>
            <TextInput
              label={t('ZIP code')}
              placeholder={t('Type code')}
              {...register('address.postalCode', {required: true})}
              error={errors.address?.postalCode}
            />
            <TextInput
              label={t('City')}
              placeholder={t('Type city')}
              {...register('address.city', {required: true})}
              error={errors.address?.city}
            />
            <TextInput name="cardNumber" label={t('Card number')} type={'number'} placeholder={t('Type your number')} />
            <TextInput name="cardName" label={t('Card holder name')} placeholder={t('Type your name')} />
            <div className="grid grid-cols-2 gap-x-3">
              <TextInput name="expiryDate" label={t('Expiry date')} placeholder={t('MM/YY')} />
              <TextInput name="securityCode" label={t('Security code')} type={'number'} placeholder={t('CCV')} />
            </div>
          </div>
          <Button type="primary" size="large" htmlType="submit" disabled={isSubmitting}>
            {t('Create account')}
          </Button>
        </form>
      </div>
    </AuthFormContainer>
  );
});
